import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { useRoutes, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Login from 'views/pages/authentication/authentication3/Login3';
import themes from 'themes';
import NavigationScroll from 'layout/NavigationScroll';
import SubAdminRoutes from 'routes/SubAdminRoutes';
import SuperAdminRoutes from 'routes/SuperAdminRoutes';
import SuperMostAdminRoutes from 'routes/SuperMostAdminRoutes';
import SuperMostAdminGatePass from 'views/SuperMostAdmin/GatePass';
import './toastStyles.css';

import MainLayout from 'layout/MainLayout';
import ResetPassword from 'views/pages/authentication/auth-forms/ResetPassword';
import { getDefaultRouteByPermissions } from 'utils/getDefaultRouteByPermissions';
import { removeStoredPermissions } from 'utils/permissionsStorage';
import { toast } from 'react-toastify';
import SafeLoginRedirect from 'utils/SafeLoginRedirect';
import ChangePassword from 'layout/MainLayout/Header/ProfileSection/ChangePassword';
import PrivacyPolicy from './views/pages/policy';
import Support from './views/pages/Support';

// ==============================|| APP2 ||============================== //

const AppRoutes = ({ role }) => {
  let routes;
  const roleLower = String(role || '').toLowerCase();

  if (roleLower === 'customer' || roleLower === 'student') {
    routes = [...SubAdminRoutes.children];
  } else if (roleLower === 'admin') {
    routes = SuperAdminRoutes.children;
  } else if (roleLower === 'superadmin') {
    routes = [...SuperMostAdminRoutes.children, { path: 'supermostadmin/studentGatePass', element: <SuperMostAdminGatePass /> }];
  } else if (roleLower) {
    // ✅ If any role is present but not matching above → show admin routes
    routes = SuperAdminRoutes.children;
  } else {
    routes = [{ path: '*', element: <Login /> }];
  }

  const element = useRoutes([
    {
      path: '/',
      element: <MainLayout />,
      children: routes
    }
  ]);

  return element;
};

const App = () => {
  const customization = useSelector((state) => state.customization);
  const location = useLocation();
  const navigate = useNavigate();
  const [isTokenExpired, setIsTokenExpired] = useState(true);
  const [role, setRole] = useState('');
  const [hasToken, setHasToken] = useState(false);
  const [page, setPage] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  const [forceChangePassword, setForceChangePassword] = useState(false);

  // Function to clear localStorage
  const clearLocalStorage = () => {
    localStorage.removeItem('lastVisitedPath');
    localStorage.removeItem('currentPage');
    // Add any other localStorage keys you want to clear
  };

  // Note: Token expiry checking is handled by cookie expiration
  // Browser automatically removes expired cookies

  // Function to handle 401 unauthorized - can be called from anywhere
  const handleUnauthorized = () => {
    // Clear cookies
    Cookies.remove('Token');
    Cookies.remove('Role');
    // Clear localStorage
    clearLocalStorage();
    // Update state
    setHasToken(false);
    setRole('');
    // Navigate to login
    navigate('/login', { replace: true });
  };

  // Make handleUnauthorized available globally
  useEffect(() => {
    window.handleUnauthorized = handleUnauthorized;
  }, [navigate]);

  // Note: Cookie expiration is set in login to match JWT token expiry
  // Browser will automatically remove expired cookies, so no manual checking needed

  useEffect(() => {
    const syncAuthFromCookies = () => {
      const token = Cookies.get('Token');
      const userRole = Cookies.get('Role');
      setRole(userRole || '');
      setHasToken(Boolean(token));
      setIsTokenExpired(!token);
    };

    const handleAuthUpdated = () => {
      syncAuthFromCookies();
    };

    window.addEventListener('auth:updated', handleAuthUpdated);
    return () => window.removeEventListener('auth:updated', handleAuthUpdated);
  }, []);

  useEffect(() => {
    const token = Cookies.get('Token');
    const userRole = Cookies.get('Role');
    const passwordChanged = Cookies.get('isPasswordChanged');
    setRole(userRole);

    const checkToken = () => {
      if (token) {
        // Token exists and browser hasn't removed it (so it's not expired)
        setHasToken(true);
        setIsTokenExpired(false);
      } else {
        // No token found (either never set or expired and removed by browser)
        setHasToken(false);
        setIsTokenExpired(true);
        // Clear localStorage when no token
        clearLocalStorage();
      }
    };
    checkToken();

    // ✅ Force password change check
    // const safeRole = typeof role === 'string' ? role.toLowerCase() : '';
    if (
      token &&
      passwordChanged === 'false'
      //  && safeRole === 'student'
    ) {
      setForceChangePassword(true);
    } else {
      setForceChangePassword(false);
    }
  }, [hasToken, role, navigate]);

  // Load saved page on refresh
  useEffect(() => {
    const savedPage = localStorage.getItem('currentPage');
    if (savedPage) {
      setPage(savedPage);
    }
  }, []);

  // Save page whenever it changes
  useEffect(() => {
    localStorage.setItem('currentPage', page);
  }, [page]);

  // Save current path to localStorage whenever location changes
  useEffect(() => {
    if (hasToken && location.pathname !== '/login' && location.pathname !== '/forgot-password') {
      localStorage.setItem('lastVisitedPath', location.pathname);
    }
  }, [location.pathname, hasToken]);

  useEffect(() => {
    if (location.pathname !== '/login') return;
    const flashMessage = sessionStorage.getItem('AUTH_FLASH_SUCCESS');
    if (!flashMessage) return;
    sessionStorage.removeItem('AUTH_FLASH_SUCCESS');
    toast.success(flashMessage);
  }, [location.pathname]);

  // Load saved path on app initialization (role-aware)
  // useEffect(() => {
  //   if (hasToken && !isInitialized) {
  //     const savedPath = localStorage.getItem('lastVisitedPath');
  //     const roleLower = String(role || '').toLowerCase();
  //     const defaultPath =
  //       roleLower === 'superadmin'
  //         ? '/supermostadmin/default'
  //         : roleLower === 'student'
  //         ? '/dashboard/default'
  //         : '/superadmindashboard/default';

  //     if (savedPath && savedPath !== '/login' && savedPath !== '/forgot-password' && savedPath !== '/') {
  //       // If saved path mismatches current role's namespace, send to the role default
  //       const mismatch =
  //         (roleLower === 'superadmin' && !savedPath.startsWith('/supermostadmin')) ||
  //         (roleLower === 'admin' && !savedPath.startsWith('/superadmindashboard')) ||
  //         ((roleLower === 'student' || roleLower === 'customer') && !savedPath.startsWith('/dashboard'));
  //       navigate(mismatch ? defaultPath : savedPath, { replace: true });
  //     } else {
  //       navigate(defaultPath, { replace: true });
  //     }
  //     setIsInitialized(true);
  //   }
  // }, [hasToken, navigate, isInitialized, role]);

  // updatyed role based routing useEffect hook

  useEffect(() => {
    if (forceChangePassword) return;
    const publicPaths = ['/privacy-policy', '/support', '/login'];

    if (publicPaths.includes(location.pathname)) {
      setIsInitialized(true);
      return;
    }

    if (hasToken && !isInitialized) {
      const savedPath = localStorage.getItem('lastVisitedPath');
      const roleLower = String(role || '').toLowerCase();

      // Determine default path based on role and permissions
      let defaultPath;
      if (roleLower === 'superadmin') {
        defaultPath = '/supermostadmin/default';
      } else if (roleLower === 'student') {
        defaultPath = '/dashboard/default';
      } else {
        // admin/other roles -> check permissions
        defaultPath = getDefaultRouteByPermissions(); // may be null
      }

      if (!defaultPath) {
        // no permission -> log out and send to login with toast
        toast.error('You have no permission to access any module. Contact administrator.');
        // clear token & role & permissions
        Cookies.remove('Token');
        Cookies.remove('Role');
        removeStoredPermissions();
        clearLocalStorage();
        navigate('/login', { replace: true });
        setIsInitialized(true);
        return;
      }

      if (savedPath && savedPath !== '/login' && savedPath !== '/forgot-password' && savedPath !== '/') {
        const mismatch =
          (roleLower === 'superadmin' && !savedPath.startsWith('/supermostadmin')) ||
          (roleLower === 'admin' && !savedPath.startsWith('/superadmindashboard')) ||
          ((roleLower === 'student' || roleLower === 'customer') && !savedPath.startsWith('/dashboard'));

        navigate(mismatch ? defaultPath : savedPath, { replace: true });
      } else {
        navigate(defaultPath, { replace: true });
      }
      setIsInitialized(true);
    }
  }, [hasToken, navigate, isInitialized, role]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes(customization)}>
        <CssBaseline />
        <ToastContainer enableMultiContainer />
        <NavigationScroll>
          {/* 🔒 Force password change middleware */}
          {forceChangePassword ? (
            <ChangePassword open={true} forceReset={true} handleClose={() => setForceChangePassword(true)} />
          ) : (
            <Routes>
              {/* default routing logic */}
              {/* <Route
              path="/login"
              element={
                hasToken ? (
                  <Navigate
                    to={(() => {
                      const r = String(role || '').toLowerCase();
                      return r === 'student'
                        ? '/dashboard/default'
                        : r === 'superadmin'
                        ? '/supermostadmin/default'
                        : '/superadmindashboard/default';
                    })()}
                    replace
                  />
                ) : (
                  <Login />
                )
              }
            /> */}

              {/* role based routing logic */}
              <Route path="/login" element={hasToken ? <SafeLoginRedirect role={role} /> : <Login />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/support" element={<Support />} />
              <Route
                path="/forgot-password"
                element={
                  hasToken ? (
                    <Navigate
                      to={(() => {
                        const r = String(role || '').toLowerCase();
                        return r === 'student'
                          ? '/dashboard/default'
                          : r === 'superadmin'
                          ? '/supermostadmin/default'
                          : '/superadmindashboard/default';
                      })()}
                      replace
                    />
                  ) : (
                    (() => {
                      // console.log('Rendering ResetPassword component for /forgot-password');
                      return <ResetPassword />;
                    })()
                  )
                }
              />
              {/* Test route for debugging */}
              {/* <Route
              path="/test-reset"
              element={<ResetPassword />}
            /> */}
              {/* Protected routes */}
              {hasToken ? (
                <Route path="/*" element={<AppRoutes role={role} />} />
              ) : (
                <Route path="/*" element={<Navigate to="/login" replace />} />
              )}
            </Routes>
          )}
        </NavigationScroll>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};
export default App;
